import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { OrderModel } from '../../../models/transaction'
import TransactionInvoiceModal from '../TransactionInvoiceModal'
import {
  addToCart,
  annulTransaction,
  cancelOngoingAction,
  creditTransaction,
  debitTransaction,
  getCartSummary,
  hasInvoiceRef,
} from '../../../store/actions/transactions-actions'
import { getText } from '../../../internal/TextLabels'
import {
  getMaxQuantity,
  isQuantityValidAgainstCartStatus,
  setupCartItems,
} from '../shared/transaction-utils'

import { PaymentMethods } from '../../../models/payment-methods'
import { format } from '../../../models/format'
import TransactionCartUIEcom from './UI/TransactionCartUIEcom'
import { ActionTypes } from '../../../util/transactionConstants'
import { getDefaultVatRate } from '../../../models/countries'
import {
  sendChangeTransactionEvent,
  sendTransactionAddRowEvent,
} from '../../../util/ga-helpers'

const debitedLessThanReservedQuantity = item =>
  Math.abs(item.debitedQuantity + item.canceledQuantity) <
  Math.abs(item.reservedQuantity)

const TransactionCartEcom = props => {
  const createStateOfTheArticles = ({ transactionCart }) => {
    const cartItems = (transactionCart || {}).cartItems || []
    if (cartItems && cartItems.length && !props.action.loading) {
      return {
        articles: setupCartItems(cartItems),
        initialArticles: cartItems,
      }
    }
    return {
      articles: [],
    }
  }

  const [action, setAction] = useState({
    type: null,
    showHandler: false,
  })

  const [hasActiveErrors, setHasActiveErrors] = useState(false)
  const [addArticle, setAddArticle] = useState(false)
  const [invoiceRef, setInvoiceRef] = useState('')
  const [sumOperations, setSumOperations] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [errors, setErrors] = useState([])
  const [stateOfTheArticles, setStateOfTheArticles] = useState(
    createStateOfTheArticles(props),
  )

  const [shouldUpdateErrors, setShouldUpdateErrors] = useState(false)
  const [updateCalculatedValues, setUpdateCalculatedValues] = useState(false)

  const [lastProps, setLastProps] = useState(props)

  const handleActionRef = useRef(false)

  const getSumTextBasedOnActionType = () => {
    if (action.type === ActionTypes.DEBIT) {
      return getText(
        'transactions.transaction.specification.newSumError',
        props.cartStatus.debitableAmount,
      )
    }
    if (action.type === ActionTypes.CREDIT) {
      return getText(
        'transactions.transaction.specification.newSumError',
        props.cartStatus.creditableAmount,
      )
    }

    return ''
  }

  const updateArticles = (items, doUpdateErrors) => {
    setStateOfTheArticles({
      articles: items,
      initialArticles: stateOfTheArticles.initialArticles,
    })
    setShouldUpdateErrors(doUpdateErrors)
  }

  const updateErrors = () => {
    // identify and update error state
    const quantityError = sumOperations
      ? false
      : stateOfTheArticles.articles.filter(item => item.reservedQuantityValid)
          .length < stateOfTheArticles.articles.length
    const newQuantityError = sumOperations
      ? false
      : stateOfTheArticles.articles.filter(item => item.newQuantityValid)
          .length < stateOfTheArticles.articles.length
    const sumError = !!stateOfTheArticles.articles.find(
      item => item.newSumValid === false,
    )

    setErrors([
      {
        name: 'quantityError',
        error: quantityError,
        message: getText(
          'transactions.transaction.specification.quantityError',
        ),
      },
      {
        name: 'newQuantityError',
        error: newQuantityError,
        message: getText(
          'transactions.transaction.specification.newQuantityError',
        ),
      },
      {
        name: 'newSumError',
        error: sumError,
        message: getSumTextBasedOnActionType(),
      },
    ])
    setHasActiveErrors(sumError || quantityError || newQuantityError)
  }

  const isArticlesFullCart = () =>
    stateOfTheArticles.articles.length ===
      stateOfTheArticles.initialArticles.length &&
    stateOfTheArticles.articles.every(
      article => parseInt(article.newQuantity, 10) === article.reservedQuantity,
    )

  const getArticles = () => {
    if (sumOperations) {
      return stateOfTheArticles.articles.slice(-1)
    }
    if (isArticlesFullCart()) {
      return []
    }
    return stateOfTheArticles.articles
  }

  const handleDebit = (theInvoiceRef = '') => {
    props.debit(getArticles(), props.transactionId, theInvoiceRef)
  }

  const handleCredit = (theInvoiceRef = '') => {
    props.credit(getArticles(), props.transactionId, theInvoiceRef)
  }

  const onClose = () => {
    setInvoiceRef('')
    setShowModal(false)
  }

  const onCancel = () => {
    setAction({
      showHandler: null,
      type: null,
    })

    setStateOfTheArticles(createStateOfTheArticles(props))
    setSumOperations(false)
    setShouldUpdateErrors(true)
  }

  const setNewQuantity = ({ index, value, isValid }) => {
    const updatedItems = [...stateOfTheArticles.articles]
    updatedItems[index].newQuantity = value
    updatedItems[index].newQuantityValid = isValid

    return updatedItems
  }

  const setNewSum = ({ index, value, isValid }) => {
    const updatedItems = [...stateOfTheArticles.articles]
    updatedItems[index].newSum = value
    updatedItems[index].newSumValid = isValid

    return updatedItems
  }

  const setNewSumDescription = ({ index, value }) => {
    const updatedItems = [...stateOfTheArticles.articles]
    updatedItems[index].newSumDescription = value

    return updatedItems
  }

  const setNewSumVat = ({ index, value }) => {
    const updatedItems = [...stateOfTheArticles.articles]
    updatedItems[index].vatRate = value / 100

    return updatedItems
  }

  const addSumRow = () => {
    const articles = setupCartItems(stateOfTheArticles.initialArticles)
    const item = {
      externalId: '',
      reference: '',
      unit: '',
      newQuantity: null,
      newAmount: null,
      newSum: 0,
      newSumDescription: getText(
        'transactions.transaction.sumAction.unspecified',
      ),
      vatRate: getDefaultVatRate(props.countryCode),
      type: 'NORMAL',
    }

    articles[articles.length] = Object.assign(new OrderModel(), item, {
      newSumValid: true,
      rowId: 'new_sum_row',
    })

    updateArticles(articles, true)
  }

  const toggleSumOperations = () => {
    if (sumOperations) {
      setUpdateCalculatedValues(true)
    } else if (
      action.type === ActionTypes.DEBIT ||
      action.type === ActionTypes.CREDIT
    ) {
      addSumRow()
    }

    setSumOperations(!sumOperations)
  }

  const allowZeroAmount = () => {
    return (
      props.transactionDetails &&
      props.transactionDetails.paymentMethod &&
      props.transactionDetails.paymentMethod.type === PaymentMethods.RESURS_ZERO
    )
  }

  const validateArticleQuantities = (alreadyCheckedIndex = -1) => {
    const updatedItems = stateOfTheArticles.articles.map((item, index) => {
      if (index === alreadyCheckedIndex && item.newQuantityValid === false) {
        return item
      }
      const updatedItem = item
      updatedItem.newQuantityValid = isQuantityValidAgainstCartStatus(
        action.type,
        updatedItem,
        stateOfTheArticles.articles,
        updatedItem.newQuantity,
        props.cartStatus,
        allowZeroAmount(),
      )
      return updatedItem
    })

    updateArticles(updatedItems)
    updateErrors()
  }

  const updateItemsWithCalculatedValues = () => {
    const theAction = action.type
    const updatedItems = stateOfTheArticles.articles.map(item => {
      const updatedItem = item
      if (!item.reservedQuantityValid) {
        updatedItem.newSum = null
        updatedItem.newSumValid = true
      } else if (!item.newSum && !sumOperations) {
        updatedItem.newQuantity = getMaxQuantity(item, theAction, true)
        updatedItem.newQuantityValid = true
      }
      return updatedItem
    })
    updateArticles(updatedItems)
    validateArticleQuantities()
  }

  const toggleAddArticle = state => {
    if (state) {
      sendTransactionAddRowEvent('Impression', props.transactionDetails)
    }
    setAddArticle(state)
  }

  const checkAvailability = (itemIndex, theAction) => {
    // check if item actions are available

    switch (theAction) {
      case ActionTypes.DEBIT:
        return (
          props.cartStatus &&
          props.cartStatus.debitable &&
          debitedLessThanReservedQuantity(
            stateOfTheArticles.articles[itemIndex],
          )
        )
      case ActionTypes.CREDIT:
        return (
          props.cartStatus &&
          props.cartStatus.creditable &&
          Math.abs(stateOfTheArticles.articles[itemIndex].creditedQuantity) <
            Math.abs(stateOfTheArticles.articles[itemIndex].debitedQuantity)
        )

      case ActionTypes.ANNUL:
        return debitedLessThanReservedQuantity(
          stateOfTheArticles.articles[itemIndex],
        )
      default:
        return false
    }
  }

  const handleNewQuantityChange = (newQuantity, index) => {
    const value = newQuantity
    const min =
      stateOfTheArticles.articles[index].reservedQuantity < 0
        ? +getMaxQuantity(stateOfTheArticles.articles[index], action.type, true)
        : 0
    const max =
      min < 0
        ? 0
        : +getMaxQuantity(stateOfTheArticles.articles[index], action.type)
    if (
      value >= min &&
      value <= max &&
      new RegExp(format.negativeOrPositiveNumberWithMax2Decimal.regex).test(
        value,
      )
    ) {
      updateArticles(setNewQuantity({ index, value, isValid: true }), true)
    } else {
      updateArticles(setNewQuantity({ index, value, isValid: false }), true)
    }

    validateArticleQuantities(index)
  }

  const handleNewSumChange = (newSum, index) => {
    const value = newSum
    const min = 0

    let max = 0
    if (action.type === ActionTypes.DEBIT) {
      max = props.cartStatus.debitableAmount
    } else if (action.type === ActionTypes.CREDIT) {
      max = props.cartStatus.creditableAmount
    }
    if (
      // eslint-disable-next-line eqeqeq
      (allowZeroAmount() && value == 0) ||
      (value > min &&
        value <= max &&
        new RegExp(format.numberWithMax2Decimal.regex).test(value))
    ) {
      updateArticles(setNewSum({ index, value, isValid: true }))
    } else {
      updateArticles(setNewSum({ index, value, isValid: false }))
    }
  }

  const handleNewSumDescriptionChange = (e, index) => {
    const value = e.target.value
    updateArticles(setNewSumDescription({ index, value }))
  }

  const handleNewSumVatChange = (value, index) => {
    updateArticles(setNewSumVat({ index, value }), true)
  }

  const nothingToUpdate = () => {
    return !stateOfTheArticles.articles.filter(
      item =>
        (item.newQuantity !== null && item.newQuantity > 0) ||
        (item.newSum !== null && item.newSum > 0) ||
        (item.newSum !== null && item.newSum === 0 && allowZeroAmount()),
    ).length
  }

  const handleAnnul = () => {
    props.annul(stateOfTheArticles.articles, props.transactionId)
  }

  const handleComplete = item => {
    sendTransactionAddRowEvent('Add', props.transactionDetails)
    const newItem = Object.assign(new OrderModel(), item)
    newItem.vatRate = item.vatRate / 100
    props.addToCart(newItem, props.transactionId)
  }

  const isInvoice = () => {
    return (
      props.transactionDetails &&
      props.transactionDetails.paymentMethod &&
      props.transactionDetails.paymentMethod.type ===
        PaymentMethods.RESURS_INVOICE
    )
  }

  const handleAction = theAction => {
    sendChangeTransactionEvent(theAction, props.transactionDetails)
    // TODO: hasInvoiceRef request will be moved to transaction-action and stored in redux.
    switch (theAction) {
      case ActionTypes.DEBIT:
        if (isInvoice() && invoiceRef === '') {
          props.hasInvoiceRef()
          break
        }
        handleDebit(invoiceRef)
        break
      case ActionTypes.CREDIT:
        if (isInvoice() && invoiceRef === '') {
          props.hasInvoiceRef()
          break
        }
        handleCredit(invoiceRef)
        break
      case ActionTypes.ANNUL:
        handleAnnul()
        break
      default:
        break
    }
  }

  const handleInvoiceSubmit = ref => {
    handleActionRef.current = true
    setInvoiceRef(ref)
    setShowModal(false)
  }

  useEffect(() => {
    if (JSON.stringify(lastProps) === JSON.stringify(props)) {
      return
    }

    if (props.invoiceRef.required !== null) {
      if (
        props.action.loading &&
        props.invoiceRef.required &&
        invoiceRef === ''
      ) {
        setShowModal(true)
      }
      if (
        props.action.loading &&
        !props.action.active &&
        !props.invoiceRef.required
      ) {
        if (action.type === ActionTypes.DEBIT) {
          handleDebit()
        } else {
          handleCredit()
        }
      }
    } else if (
      props.transactionCart &&
      props.transactionCart.cartItems &&
      props.transactionCart.cartItems.length > 0 &&
      !props.action.loading
    ) {
      setStateOfTheArticles(createStateOfTheArticles(props))
      setAction({
        type: null,
        showHandler: false,
      })
      setSumOperations(false)
      setInvoiceRef('')
    }
    setLastProps(props)
  })

  useEffect(() => {
    if (props.transactionId) {
      props.getCartSummary(props.transactionId)
    }
  }, [])

  useEffect(() => {
    if (shouldUpdateErrors) {
      updateErrors()
      setShouldUpdateErrors(false)
    }
  }, [stateOfTheArticles])

  useEffect(() => {
    if (!showModal) {
      props.cancelOngoingAction()
    }
  }, [showModal])

  useEffect(() => {
    if (
      (action === ActionTypes.DEBIT && sumOperations) ||
      (action === ActionTypes.CREDIT && sumOperations)
    ) {
      addSumRow()
    } else if (action === ActionTypes.ANNUL && sumOperations) {
      toggleSumOperations()
    } else {
      updateItemsWithCalculatedValues()
    }
  }, [action])

  useEffect(() => {
    if (updateCalculatedValues) {
      updateItemsWithCalculatedValues()
      setUpdateCalculatedValues(false)
    }
  }, [updateCalculatedValues])

  useEffect(() => {
    if (handleActionRef.current) {
      handleAction.current = false
      handleAction(action.type)
    }
  }, [invoiceRef])

  return (
    <div>
      <TransactionCartUIEcom
        actionState={props.action}
        cartStatus={props.cartStatus}
        errors={errors}
        activeErrors={hasActiveErrors}
        cartArticles={stateOfTheArticles.articles}
        orderLimit={props.orderLimit}
        addArticle={addArticle}
        action={action}
        disablePerformButton={nothingToUpdate()}
        toggleAddArticle={state => toggleAddArticle(state)}
        checkAvailability={(index, theAction) =>
          checkAvailability(index, theAction)
        }
        handleNewQuantityChange={(e, index) => {
          handleNewQuantityChange(e, index)
          updateErrors()
        }}
        handleNewSumChange={(e, index) => {
          handleNewSumChange(e, index)
          updateErrors()
        }}
        setAction={theAction =>
          setAction({
            ...action,
            type: theAction,
          })
        }
        handleComplete={item => handleComplete(item)}
        handleAction={theAction => handleAction(theAction)}
        isInvoice={() => isInvoice()}
        transactionDetails={props.transactionDetails}
        sumOperations={sumOperations}
        toggleSumOperations={() => toggleSumOperations()}
        onCancel={() => onCancel()}
        handleNewSumDescriptionChange={(e, index) =>
          handleNewSumDescriptionChange(e, index)
        }
        handleNewSumVatChange={(value, index) =>
          handleNewSumVatChange(value, index)
        }
      />

      <TransactionInvoiceModal
        show={showModal}
        handleSubmit={ref => handleInvoiceSubmit(ref)}
        onClose={() => onClose()}
      />
    </div>
  )
}

TransactionCartEcom.propTypes = {
  getCartSummary: PropTypes.func.isRequired,
  addToCart: PropTypes.func.isRequired,
  debit: PropTypes.func.isRequired,
  credit: PropTypes.func.isRequired,
  annul: PropTypes.func.isRequired,
  cancelOngoingAction: PropTypes.func.isRequired,
  hasInvoiceRef: PropTypes.func.isRequired,
  orderLimit: PropTypes.any.isRequired,
  cartStatus: PropTypes.object,
  transactionId: PropTypes.string,
  transactionCart: PropTypes.object, //eslint-disable-line
  action: PropTypes.object,
  invoiceRef: PropTypes.object, //eslint-disable-line
  transactionDetails: PropTypes.object,
  countryCode: PropTypes.string.isRequired,
}

TransactionCartEcom.defaultProps = {
  transactionId: null,
  transactionCart: null,
  cartStatus: null,
  transactionDetails: null,
  action: { loading: false },
  invoiceRef: {
    required: false,
  },
}

const mapStateToProps = state => {
  return {
    transactionId: state.transactions.currentTransactionId,
    transactionCart: state.transactions.currentTransactionCart.cart,
    cartStatus: state.transactions.currentTransactionCart.cartStatus,
    action: state.transactions.action,
    invoiceRef: state.transactions.invoiceRef,
    countryCode: state.storeInfo.currentRepresentative.country,
  }
}

const mapDispatchToProps = dispatch => ({
  getCartSummary: transactionId => {
    dispatch(getCartSummary(transactionId))
  },
  addToCart: (cartItem, transactionId) => {
    dispatch(addToCart(cartItem, transactionId))
  },
  debit: (data, transactionId, invoiceRef) => {
    dispatch(debitTransaction(data, transactionId, invoiceRef))
  },
  credit: (data, transactionId, invoiceRef) => {
    dispatch(creditTransaction(data, transactionId, invoiceRef))
  },
  annul: (data, transactionId) => {
    dispatch(annulTransaction(data, transactionId))
  },
  hasInvoiceRef: () => {
    dispatch(hasInvoiceRef())
  },
  cancelOngoingAction: () => {
    dispatch(cancelOngoingAction())
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(TransactionCartEcom)
